// React
import React, { useEffect, useState, useMemo, useCallback } from "react";
// PropTypes
import PropTypes from "prop-types";
// Semantic
import { Button, Dropdown, Icon, Label, Checkbox } from "semantic-ui-react";
// Lodash
import { get, map, isEmpty, find, isArray, filter, concat } from "lodash";
// Utils
import { dropdownFormat } from "@Utils/formats";
// Connect
import { connect } from "react-redux";
// Sortable
import { SortableElement } from "react-sortable-hoc";
// Components
import DragHandle from "@Features/ChatsBots/components/DragHandle";
import TextAreaInput from "@Components/TextArea";
import Text from "@Components/Text";
import Input from "@Components/Input";
import { buildActionOptions, getActionDef, ensureCurrentActionOption, fieldKey, fieldLabel } from "./functions";
import FileManager from "@Components/FileManager";
import UploadFile from "@Components/uploadFile";

// Styles
import styles from "./styles.module.css";

const SortableOption = SortableElement(
	({
		option,
		stages,
		stage,
		stageId,
		updateOption,
		isRemove,
		pocition,
		errors,
		moveOption,
		optionsLength,
		modalOption,
		actionsList,
		groups,
	}) => {
		const [optionEnlace, setOptionEnlace] = useState([]);
		const [groupsOptions, setGroupsOptions] = useState([]);
		const [localFieldValues, setLocalFieldValues] = useState({});

		useEffect(() => {
			const filteredStages = stages.filter((s) => {
				return s.id !== stage.id && s.type !== "menu";
			});
			const dataOption = dropdownFormat(filteredStages, "name", "id");
			setOptionEnlace(dataOption);
		}, [stages, stage.id]);

		// Cargar grupos de la compañía
		useEffect(() => {
			if (groups && groups.length > 0) {
				const formattedGroups = groups.map((group, idx) => {
					return {
						key: `group-${idx}`,
						text: group.name || group.groupName,
						value: group.id || group.groupId || group.name || group.groupName,
					};
				});
				setGroupsOptions(formattedGroups);
			} else {
				setGroupsOptions([]);
			}
		}, [groups]);

		const actionOptions = useMemo(() => {
			const base = buildActionOptions(actionsList);
			return ensureCurrentActionOption(base, option.action);
		}, [actionsList, option.action]);

		const actionDef = useMemo(() => {
			return getActionDef(actionsList, option.action);
		}, [actionsList, option.action]);

		const staticFields = useMemo(() => {
			return (actionDef?.fields || []).filter((f) => {
				return f?.acquisition === "static" || f?.acquisition === "generated";
			});
		}, [actionDef]);

		const getCurrentActionId = useMemo(() => {
			if (!option.action) return null;

			const matchingOption = actionOptions.find((opt) => {
				return (
					opt.actionData &&
					opt.actionData.name === option.action.name &&
					opt.actionData.type === option.action.type &&
					opt.actionData.function === option.action.function
				);
			});

			return matchingOption ? matchingOption.value : null;
		}, [option.action, actionOptions]);

		// Cambiar la acción seleccionada
		const onActionChange = (_, { value }) => {
			if (!value) {
				updateOption(stageId, option.id, "action", undefined);
				return;
			}

			const fullAction = find(actionsList, { id: value });
			if (!fullAction) return;

			updateOption(stageId, option.id, "action", {
				...fullAction,
				fields:
					fullAction.fields?.map((field) => {
						// Si el campo es "type" y no tiene valor, establecer "Text" como default
						if (field.name === "type" && !field.value) {
							return { ...field, value: "Text" };
						}
						return { ...field, value: field.value };
					}) || [],
			});
		};

		// Actualiza un campo específico de la acción
		const setPayload = useCallback(
			(key, val) => {
				const action = option.action ?? {};
				const updatedFields =
					action.fields?.map((field) => {
						return field.name === key ? { ...field, value: val } : field;
					}) || [];

				updateOption(stageId, option.id, "action", {
					...action,
					fields: updatedFields,
				});
			},
			[stageId, option.id, option.action, updateOption]
		);

		// Actualiza múltiples campos de la acción de una vez
		const setPayloadMultiple = useCallback(
			(changes) => {
				const action = option.action ?? {};
				const updatedFields =
					action.fields?.map((field) => {
						const change = changes.find((c) => {
							return c.key === field.name;
						});
						return change ? { ...field, value: change.val } : field;
					}) || [];

				updateOption(stageId, option.id, "action", {
					...action,
					fields: updatedFields,
				});
			},
			[stageId, option.id, option.action, updateOption]
		);

		// obtiene error ya indexado por path
		const getErr = useCallback(
			(path) => {
				return get(find(errors.optionError, { stageId: stage.id, optionId: option.id }), ["error", path], "");
			},
			[errors, stage.id, option.id]
		);

		const getErrors = useCallback(
			(errorName) => {
				const errorObject = find(errors.optionError, { stageId: stage.id, optionId: option.id });
				return get(errorObject, ["error", errorName], "");
			},
			[errors.optionError, stage.id, option.id]
		);

		const staticFieldEntries = useMemo(() => {
			return map(staticFields, (f) => {
				const k = fieldKey(f);
				const lbl = fieldLabel(f);
				const fieldWithValue = option?.action?.fields?.find((field) => {
					return field.name === k;
				});
				// Usar valor local si existe, sino el valor del objeto option
				let val = localFieldValues[k] ?? fieldWithValue?.value ?? "";
				if (k === "type" && !val) {
					val = "Text";
					setLocalFieldValues((prev) => {
						if (!prev[k]) {
							return { ...prev, [k]: "Text" };
						}
						return prev;
					});
					if (!fieldWithValue?.value) {
						setPayload(k, "Text");
					}
				}

				return { k, lbl, val, typ: f.type };
			});
		}, [staticFields, option?.action?.fields, localFieldValues]);

		const onStaticChange = (k, typ) => {
			return (e) => {
				const raw = e.target.value;
				const value = typ === "number" && raw ? Number(raw) : raw;

				// Actualizar estado local inmediatamente - convertir string vacío a null
				const localValue = value === "" ? null : value;
				setLocalFieldValues((prev) => {
					return {
						...prev,
						[k]: localValue,
					};
				});

				if (k === "type" && value === "Text") {
					// Actualizar estado local para incluir uri: null
					setLocalFieldValues((prev) => {
						return {
							...prev,
							[k]: localValue,
							uri: null,
						};
					});
					// Enviar ambos cambios en una sola llamada
					setPayloadMultiple([
						{ key: k, val: localValue },
						{ key: "uri", val: null },
					]);
				} else {
					setPayload(k, localValue);
				}
			};
		};

		const isImageUri = (u) => {
			return typeof u === "string" && (/^data:image\//.test(u) || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(u));
		};

		const getImageUris = (uri) => {
			return isArray(uri) ? filter(uri, isImageUri) : [];
		};
		const getFileUri = (uri) => {
			return isArray(uri)
				? find(uri, (u) => {
						return !isImageUri(u);
				  }) || null
				: null;
		};

		const handleDocumentChange = useCallback((val, fieldFiles, setPayloadFn, k) => {
			const currentImages = isArray(fieldFiles) ? filter(fieldFiles, isImageUri) : [];

			if (val) {
				setPayloadFn(k, concat(currentImages, val));
			} else {
				setPayloadFn(k, currentImages.length > 0 ? currentImages : null);
			}
		}, []);

		const handleImageChange = useCallback((nextImages, setPayloadFn, k) => {
			const onlyImages = isArray(nextImages) ? filter(nextImages, isImageUri) : [];
			setPayloadFn(k, onlyImages.length > 0 ? onlyImages : null);
		}, []);

		return (
			<fieldset className={`${styles.fieldsetOptions} no-select`}>
				<div className={styles.optionHeader}>
					<DragHandle />
					<div className={styles.contentIconDeleteStages}>
						<Icon
							name="trash alternate outline"
							className={styles.iconDeleteStages}
							disabled={modalOption === "Watch"}
							onClick={() => {
								return isRemove(stageId, "option", option.id, option.orden, stage, option);
							}}
						/>
					</div>
					<div className={styles.optionTitle}>
						<span className={styles.title}>Opción:</span>
					</div>
					<span className={styles.optionLabel}>
						{option.label} - {option.id}
					</span>

					<Checkbox
						toggle
						label={option.isActive ?? true ? "Activa" : "Inactiva"}
						checked={option.isActive ?? true}
						disabled={modalOption === "Watch"}
						onChange={(_, { checked }) => {
							updateOption(stageId, option.id, "isActive", checked);
						}}
					/>

					<Button
						className={`${styles.headerButton}${pocition === 0 ? ` ${styles.headerButtonDisabled}` : ""}`}
						disabled={pocition === 0 || modalOption === "Watch"}
						onClick={() => {
							if (pocition > 0) moveOption(pocition, pocition - 1);
						}}>
						<Icon name="minus" />
					</Button>
					<input type="text" value={pocition + 1} className={styles.idBox} readOnly />
					<Button
						className={`${styles.headerButton}${pocition === optionsLength - 1 ? ` ${styles.headerButtonDisabled}` : ""}`}
						disabled={pocition === optionsLength - 1 || modalOption === "Watch"}
						onClick={() => {
							if (pocition < optionsLength - 1) moveOption(pocition, pocition + 1);
						}}>
						<Icon name="plus" />
					</Button>

					{getErrors("label") && (
						<Label basic color="red" className={styles.errorLabel}>
							{getErrors("label")}
						</Label>
					)}
					{getErrors("action") && (
						<Label basic color="red" className={styles.errorLabel}>
							{getErrors("action")}
						</Label>
					)}
					{getErrors("document") && (
						<Label basic color="red" className={styles.errorLabel}>
							{getErrors("document")}
						</Label>
					)}
					<Icon
						name={option.isOpen ? "angle down" : "angle up"}
						onClick={() => {
							return updateOption(stageId, option.id, "isOpen", !option.isOpen);
						}}
						className={`${styles.toggleIcon} ${styles.toggleRight}`}
					/>
				</div>

				{option.isOpen && (
					<div>
						<div className={styles.optionContainer}>
							<div className={styles.fieldOption}>
								<Text className={styles.text}>
									Nombre:<span className={styles.requiredAsterisk}>*</span>
								</Text>
								<Input
									placeholder="Nombre de la opcion"
									value={option.label}
									maxLength={24}
									disabled={modalOption === "Watch"}
									onChange={(e) => {
										return updateOption(stageId, option.id, "label", e.target.value);
									}}
								/>
								{!isEmpty(getErrors("label")) && (
									<Label basic pointing color="red" className={styles.errorLabel}>
										{getErrors("label")}
									</Label>
								)}
							</div>
							<div className={styles.fieldOption}>
								<Text className={styles.text}>
									Acción:<span className={styles.requiredAsterisk}>*</span>
								</Text>

								<Dropdown
									placeholder="Selecciona una Acción..."
									fluid
									selection
									clearable
									options={actionOptions}
									value={getCurrentActionId}
									onChange={onActionChange}
									disabled={modalOption === "Watch"}
								/>

								{!isEmpty(getErr("action")) && (
									<Label basic pointing color="red" className={styles.errorLabel}>
										{getErr("action")}
									</Label>
								)}
							</div>
							{map(staticFieldEntries, ({ k, lbl, typ }) => {
								if (typ === "menu") {
									return (
										<div key={k} className={styles.fieldOption}>
											<Text className={styles.text}>{lbl}:</Text>
											<Dropdown
												placeholder="Selecciona un Menú"
												uid
												selection
												clearable
												disabled={modalOption === "Watch"}
												value={option.stageId}
												options={optionEnlace}
												onChange={(e, { value }) => {
													return updateOption(stageId, option.id, "stageId", value);
												}}
											/>
										</div>
									);
								}
								return null;
							})}
						</div>
						{(() => {
							const visibleFields = staticFieldEntries.filter(({ typ }) => {
								return typ !== "file" && typ !== "menu";
							});
							return visibleFields.length > 0;
						})() && (
							<fieldset className={styles.actionFieldsContainer}>
								<legend className={styles.actionFieldsTitle}>
									<Text className={styles.actionFieldsLegend}>Campos de la Acción</Text>
								</legend>

								<div
									className={styles.fieldOptionAuto}
									style={{
										gridTemplateColumns:
											staticFieldEntries.length === 1
												? "repeat(2, 1fr)"
												: staticFieldEntries.length === 3
												? "repeat(3, 1fr)"
												: "repeat(2, 1fr)",
									}}>
									{map(staticFieldEntries, ({ k, lbl, val, typ }) => {
										if (typ === "string") return null;
										if (typ === "file") return null;
										if (typ === "menu") return null;

										if (typ === "dropdown") {
											const fieldDef = actionDef?.fields?.find((f) => {
												return fieldKey(f) === k;
											});
											const dropdownOptions = fieldDef?.inputOptions || [];

											const formattedOptions = dropdownOptions.map((opt, idx) => {
												return {
													key: `opt-${idx}`,
													text: opt,
													value: opt,
												};
											});

											return (
												<div key={k} className={styles.fieldOption}>
													<Text className={styles.text}>{lbl}:</Text>
													<Dropdown
														placeholder={`Selecciona ${lbl}`}
														uid
														selection
														clearable
														disabled={modalOption === "Watch"}
														value={val}
														options={formattedOptions}
														onChange={(e, { value }) => {
															return onStaticChange(k, "dropdown")({ target: { value } });
														}}
													/>
												</div>
											);
										}

										if (typ === "group") {
											return (
												<div key={k} className={styles.fieldOption}>
													<Text className={styles.text}>{lbl}:</Text>
													<Dropdown
														placeholder={`Selecciona ${lbl}`}
														uid
														selection
														clearable
														disabled={modalOption === "Watch"}
														value={val}
														options={groupsOptions}
														onChange={(e, { value }) => {
															return onStaticChange(k, "group")({ target: { value } });
														}}
													/>
												</div>
											);
										}

										return (
											<div key={k} className={styles.fieldOption}>
												<Text className={styles.text}>{lbl}:</Text>
												<Input
													type={typ === "number" ? "number" : typ === "date" ? "date" : "text"}
													placeholder={typ === "number" ? `Valor para "${lbl}"` : `Ingresa ${lbl}`}
													value={val}
													disabled={modalOption === "Watch"}
													onChange={onStaticChange(k, typ)}
													className={typ === "number" ? styles.inputFieldNumber : styles.inputField}
												/>
											</div>
										);
									})}
								</div>
								<div className={styles.textAreaContainer}>
									{map(staticFieldEntries, ({ k, lbl, val, typ }) => {
										if (typ === "string") {
											return (
												<div
													key={k}
													className={styles.fieldOptionString}
													style={{
														width: staticFieldEntries.length === 1 ? "50%" : "50%",
													}}>
													<Text className={styles.text}>{lbl}:</Text>
													<TextAreaInput
														placeholder={`Ingresa ${lbl}`}
														value={val}
														disabled={modalOption === "Watch"}
														onChange={onStaticChange(k, "string")}
														className={styles.textAreaInputString}
													/>
												</div>
											);
										}
										return null;
									})}
								</div>
								<div className={styles.fieldOption}>
									{map(
										staticFieldEntries.filter((f) => {
											return f.typ === "file";
										}),
										({ k, lbl }) => {
											// Verificar si el campo "type" tiene valor "Text" para ocultar el adjunto
											// Usar valor local si existe, sino el valor del objeto option
											const typeFieldValue =
												localFieldValues.type ??
												option?.action?.fields?.find((field) => {
													return field.name === "type";
												})?.value;
											const shouldHideAttachment = typeFieldValue === "Text";

											// Si el tipo es "Text", no mostrar el campo de adjunto (incluyendo el label)
											if (shouldHideAttachment) {
												return null;
											}

											const fieldWithValue = option?.action?.fields?.find((field) => {
												return field.name === k;
											});
											const rawValue = fieldWithValue?.value;

											const fieldFiles = typeof rawValue === "string" && rawValue ? [rawValue] : isArray(rawValue) ? rawValue : [];

											const fieldImageUris = getImageUris(fieldFiles);
											const fieldFileUri = getFileUri(fieldFiles);
											// const fieldHasImages = fieldImageUris.length > 0; // REMOVIDO: no se usa más
											// const fieldHasFile = !!fieldFileUri; // REMOVIDO: no se usa más

											// Determinar qué controles mostrar basado en el tipo
											const isImageType = typeFieldValue === "Image";
											const isAudioType = typeFieldValue === "Audio";
											const isVideoType = typeFieldValue === "Video";
											const isDocumentType = typeFieldValue === "Document";

											return (
												<div key={k} className={styles.fieldOptionss}>
													<div className={styles.containerImagen}>
														<Text className={styles.text}>{lbl}:</Text>
														<div className={styles.containerImagenss}>
															{/* Mostrar FileManager solo para tipo Image */}
															{isImageType && (
																<div className={styles.classImage}>
																	<FileManager
																		images={fieldImageUris}
																		setImages={(nextImages) => {
																			return handleImageChange(nextImages, setPayload, k);
																		}}
																		disabled={modalOption === "Watch"}
																	/>
																</div>
															)}

															{/* Mostrar UploadFile para otros tipos */}
															{(isAudioType || isVideoType || isDocumentType) && (
																<div className={styles.upload}>
																	<UploadFile
																		document={fieldFileUri || null}
																		setDocument={(val) => {
																			return handleDocumentChange(val, fieldFiles, setPayload, k);
																		}}
																		accept={isAudioType ? ".mp3,.wav,.ogg" : isVideoType ? ".mp4,.avi,.mov" : ".pdf,.doc,.docx"}
																		disabled={modalOption === "Watch"}
																	/>
																</div>
															)}

															{/* Mostrar ambos controles si no hay tipo específico */}
															{!isImageType && !isAudioType && !isVideoType && !isDocumentType && (
																<>
																	<div className={styles.classImage}>
																		<FileManager
																			images={fieldImageUris}
																			setImages={(nextImages) => {
																				return handleImageChange(nextImages, setPayload, k);
																			}}
																			disabled={modalOption === "Watch"}
																		/>
																	</div>
																	<div className={styles.upload}>
																		<UploadFile
																			document={null}
																			setDocument={(val) => {
																				if (val) {
																					setPayload(k, [val]);
																				} else {
																					setPayload(k, []);
																				}
																			}}
																			accept=".pdf,.mp4,.mp3"
																			disabled={modalOption === "Watch"}
																		/>
																	</div>
																</>
															)}
														</div>
														{!isEmpty(getErr("document")) && (
															<Label basic pointing color="red" className={styles.errorLabel}>
																{getErr("document")}
															</Label>
														)}
													</div>
												</div>
											);
										}
									)}
								</div>
							</fieldset>
						)}

						<div className={styles.textArea}>
							<div className={styles.textArea}>
								<TextAreaInput
									label="Descripción:"
									name="description"
									value={option.description || ""}
									placeholder="Escribe la descripción"
									onChange={(e) => {
										if (e.target.value.length <= 72) {
											updateOption(stageId, option.id, "description", e.target.value);
										}
									}}
									disabled={modalOption === "Watch"}
									error={getErrors("description") || ""}
									showCount
									maxLength={72}
									minRows={1}
								/>
							</div>
							{!isEmpty(getErrors("description")) && (
								<Label basic color="red" className={styles.errorLabel}>
									{getErrors("description")}
								</Label>
							)}
						</div>
					</div>
				)}
			</fieldset>
		);
	}
);

SortableOption.propTypes = {
	option: PropTypes.object.isRequired,
	stage: PropTypes.object.isRequired,
	stages: PropTypes.array.isRequired,
	stageId: PropTypes.number.isRequired,
	pocition: PropTypes.number.isRequired,
	updateOption: PropTypes.func.isRequired,
	isRemove: PropTypes.func.isRequired,
	errors: PropTypes.object,
	moveOption: PropTypes.func.isRequired,
	optionsLength: PropTypes.number.isRequired,
	modalOption: PropTypes.string,
	actionsList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	groups: PropTypes.array,
};

const mapStateToProps = (state) => {
	return {
		groups: state.company?.groups || state.user?.groups || [],
	};
};

const ConnectedSortableOption = connect(mapStateToProps)(SortableOption);

export default ConnectedSortableOption;
